// Round 17 — APPROVED FINAL wording (approved 2026-06-12), reproduced VERBATIM.
//
// Single source of truth for the signature-attestation certifications, the two
// parent/guardian notice emails, and the application-PDF audit-metadata line.
// Imported by the Step 5 UI, the email templates, the PDF generator, and the
// Round 17 tests so the punctuation can never drift between what is tested and
// what ships.
//
// DO NOT edit, reword, summarize, or "fix" the punctuation below (it uses
// typographic apostrophes/dashes exactly as approved). The same wording is used
// for BOTH programs; only the program name differs, substituted at render time
// in place of PROGRAM_NAME_TOKEN.

/**
 * The literal token that stands in for the program name in the approved text.
 * The approved documents write the pair "Maryland Boys State / Maryland Girls
 * State"; each portal substitutes its own PROGRAM_NAME for this token.
 */
export const PROGRAM_NAME_TOKEN = "Maryland Boys State / Maryland Girls State";

/** Replaces the approved program-name token with the running program's name. */
export function withProgramName(text: string, programName: string): string {
  return text.split(PROGRAM_NAME_TOKEN).join(programName);
}

// --- §1 Applicant Certification (no program token, no placeholders) ----------

export const APPLICANT_CERTIFICATION =
  "I certify that the applicant signature(s), typed name(s), checkbox acknowledgments, and electronic submissions I provided in this application are my own, were entered or adopted by me with the intent to sign, and were made knowingly and voluntarily. I understand that this certification becomes part of the official application record. I agree that these electronic signatures and records may be used for this application process and have the same effect as handwritten signatures.";

// --- §2 Parent/Guardian Certification (contains the program-name token) ------

export const PARENT_CERTIFICATION =
  "I certify that I am the parent or legal guardian of the applicant and have authority to provide the authorizations and consents contained in this application, that the parent/guardian signature(s), typed name(s), checkbox acknowledgments, and electronic submissions provided by me in this application are my own, were entered or adopted by me with the intent to sign, and were made knowingly and voluntarily. I authorize my student’s participation in " +
  PROGRAM_NAME_TOKEN +
  " and provide the financial-responsibility, liability, media, and medical-treatment authorizations included in this application. I agree that these electronic signatures and records may be used for this application process and have the same effect as handwritten signatures.";

/** Applicant certification text (verbatim — no substitution needed). */
export function applicantCertificationText(): string {
  return APPLICANT_CERTIFICATION;
}

/** Parent/guardian certification text with the program name substituted in. */
export function parentCertificationText(programName: string): string {
  return withProgramName(PARENT_CERTIFICATION, programName);
}

// --- §3 Parent/Guardian Notice — first email (parent section first saved) ----
// No Reference Number (Option A). Placeholders: [student name], [support email].

export const PARENT_NOTICE_FIRST =
  "Parent/Guardian Notice — please review. An application to " +
  PROGRAM_NAME_TOKEN +
  " has been started for [student name], using this email address as the parent/guardian contact. The application includes parent/guardian certifications and authorizations, including financial responsibility, a liability release/waiver, a photo/media release, and a medical-treatment authorization, which require a parent or guardian to provide an electronic signature and to attest that those signatures are their own, and which reflect your consent to your student’s participation. If you completed and signed those sections, no action is required. Please retain this email for your records. If you did not authorize this application, did not provide these signatures, or believe it was submitted without your knowledge, please contact us right away at [support email].";

// --- §4 Parent/Guardian Notice — second email (after final submission) -------
// Placeholders: [student name], [reference number], [submission date and time],
// [program start – end dates], [support email].

export const PARENT_NOTICE_SUBMITTED =
  "Parent/Guardian Notice — application submitted. An application to " +
  PROGRAM_NAME_TOKEN +
  " has been submitted for [student name], using this email address as the parent/guardian contact.\n\n" +
  "Reference Number: [reference number]\n" +
  "Submitted: [submission date and time]\n" +
  "Program dates: [program start – end dates]\n\n" +
  "The application includes parent/guardian certifications and authorizations — financial responsibility, a liability release/waiver, a photo/media release, and a medical-treatment authorization — that require a parent or guardian to provide an electronic signature and to attest that those signatures are their own, and which reflect your consent to your student’s participation. If you completed and signed those sections, no action is required. Please retain this email for your records. If you did not authorize this application, did not provide these signatures, or believe it was submitted without your knowledge, please contact us right away at [support email].";

// --- §5 Application PDF — audit-metadata line --------------------------------

export const PDF_AUDIT_METADATA_LINE =
  "Electronic signature metadata for this submission — including acknowledgment timestamps and the submitter’s network and device information — is recorded and retained in the program’s administrative audit records.";

export interface ParentNotice1Fields {
  programName: string;
  studentName: string;
  supportEmail: string;
}

export interface ParentNotice2Fields extends ParentNotice1Fields {
  referenceNumber: string;
  submittedDateTime: string;
  programDates: string;
}

/** Fills the first (pre-submission) parent notice; returns verbatim plain text. */
export function parentNotice1Text(f: ParentNotice1Fields): string {
  return withProgramName(PARENT_NOTICE_FIRST, f.programName)
    .split("[student name]").join(f.studentName)
    .split("[support email]").join(f.supportEmail);
}

/** Fills the second (post-submission) parent notice; returns verbatim plain text. */
export function parentNotice2Text(f: ParentNotice2Fields): string {
  return withProgramName(PARENT_NOTICE_SUBMITTED, f.programName)
    .split("[student name]").join(f.studentName)
    .split("[reference number]").join(f.referenceNumber)
    .split("[submission date and time]").join(f.submittedDateTime)
    .split("[program start – end dates]").join(f.programDates)
    .split("[support email]").join(f.supportEmail);
}
